import { CanceledError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { RequestCancelAxios } from 'shared/api/lib'
import {
  type IResponseWorkflowExecutionItem,
  WorkflowExecution,
  WorkflowsApi,
} from 'entities/Workflow'
import {
  ConversationsApi,
  type IParamsConversationState,
  type IResponseGetConversationsByIdStatesItem,
} from 'entities/Conversation'

export class ChatbotExecutionStore {
  constructor() {
    makeAutoObservable(this)
  }

  private _loading = false
  private _cancelSyncExecutionRequest = new RequestCancelAxios()
  private _executionsMap = new Map<string, WorkflowExecution>()

  get executions() {
    return Array.from(this._executionsMap.values())
  }

  get isDisabledMessageForm() {
    return this.executions.some((item) => item.isLock)
  }

  changeConversationState = async (
    conversationId: number,
    execution: WorkflowExecution,
    params: IParamsConversationState
  ) => {
    if (this._loading) return

    runInAction(() => {
      this._loading = true
    })

    try {
      await ConversationsApi.changeConversationStateById(conversationId, params)

      if (params.state === 'resume') {
        toastStore.add({
          type: 'info',
          title: 'AI Agent resumed',
        })
      }
      if (params.state === 'claim') {
        runInAction(() => {
          this._executionsMap.delete(params.initiator_id)
        })
      }
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._loading = false
        execution.setLoading(false)
      })
    }
  }

  syncLatestExecutions = async (conversationId: number) => {
    if (!conversationId) return

    this._cancelSyncExecutionRequest.init()

    runInAction(() => {
      this._loading = true
    })

    try {
      const response = await ConversationsApi.getConversationsByIdStates(
        conversationId,
        {
          state_types: ['Locked'],
          statuses: ['Active', 'Paused'],
        },
        {
          cancelToken: this._cancelSyncExecutionRequest.token,
        }
      )

      const states = response.data.data.filter(
        (item) => item.initiator_type === 'workflow_execution'
      )
      const ids = states.map((state) => state.initiator_id)

      if (!ids.length) {
        this._syncExecutions([])
        return
      }

      const responseExecutionsList = await WorkflowsApi.updateWorkflowsExecutionsList(
        {
          ids: ids,
        },
        {
          cancelToken: this._cancelSyncExecutionRequest.token,
        }
      )

      const executions = states.map((state) => {
        const executionResponse = responseExecutionsList.data.payload.find(
          (t) => t.id === state.initiator_id
        )

        return {
          state: state,
          execution: executionResponse || null,
        }
      })

      this._syncExecutions(executions)
    } catch (error) {
      if (error instanceof CanceledError) return

      runInAction(() => this._executionsMap.clear())

      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._cancelSyncExecutionRequest.reset()
    this._executionsMap.clear()
  }

  private _syncExecutions = (
    data: {
      execution: IResponseWorkflowExecutionItem | null
      state: IResponseGetConversationsByIdStatesItem
    }[]
  ) => {
    this._executionsMap.clear()

    data.forEach(({ execution, state }) => {
      const actualExecution = this._executionsMap.get(state.initiator_id)

      if (actualExecution) actualExecution.syncOrigin(state, execution)
      else this._executionsMap.set(state.initiator_id, new WorkflowExecution(state, execution))
    })
  }

  get loading() {
    return this._loading
  }
}
